import {
  type Content,
  type ContentHref,
  type PersonHref,
  isContentHref,
  isReferencesRelation,
} from '@generalTypes/apiTypes';
import type { ContentRelationsMap } from '@generalTypes/generalTypes';
import type { Person } from '@generalTypes/personApiTypes';
import {
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectContentItem,
} from '@newStore/documentApi/documentApiSelectors';
import type { ContentRecord, RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { getRoot } from '@newStore/externalData/externalDataHelpers';
import {
  selectAllExternalData,
  selectExternalContent,
  selectExternalContentRelationsMap,
  selectFailedOrMissingResources,
  selectPersons,
} from '@newStore/externalData/externalDataSelectors';
import type { AllExternalData } from '@newStore/externalData/externalDataTypes';
import { createTypedSelector } from '@newStore/genericHelpers';
import { NodeType } from '@nodeTypeConfig/configTypes';
import { referenceGroupTypes } from '@store/constants/constants';
import type {
  PersonReferenceNode,
  ReferenceGroupNode,
  ReferenceGroupReference,
  ReferenceNode,
} from '../documentUITypes';
import { selectProposedContentHrefsToDelete } from '../transformProposal/proposalSelectors';

const getReferenceNode = (
  relationsMap: RelationsToAndFromMap,
  href: ContentHref,
  externalData: AllExternalData,
  externalRelationsMap: ContentRelationsMap,
  externalContent: ContentRecord,
): Omit<ReferenceGroupReference, 'isDeletedByProposal'> | null => {
  const referenceRelation = relationsMap.from[href].find(isReferencesRelation);
  const referenceHref = referenceRelation?.to.href;
  const reference = referenceHref && (externalData[referenceHref] as Content);
  if (!reference) {
    return null;
  }
  const type =
    referenceGroupTypes[reference.type] ||
    referenceGroupTypes[reference.$$meta.type] ||
    referenceGroupTypes.WEBPAGE2;
  const displayFuncOrProp = type.display;
  let title =
    typeof displayFuncOrProp === 'function'
      ? displayFuncOrProp(reference)
      : reference[displayFuncOrProp];
  const root =
    isContentHref(referenceHref) && getRoot(externalRelationsMap, externalContent, referenceHref);
  if (root) {
    title += `(${root.title})`;
  }

  return {
    referenceRelationHref: referenceRelation.$$meta.permalink,
    referenceNodeHref: href,
    icon: type.icon,
    href: referenceHref,
    title,
    identifier: Array.isArray(reference.identifiers) ? reference.identifiers[0] : undefined,
    color: reference.color,
  };
};

export const getReferenceNodes = (
  relationsMap: RelationsToAndFromMap,
  href: ContentHref,
  externalData: AllExternalData,
  externalRelationsMap: ContentRelationsMap,
  externalContent: ContentRecord,
  proposedHrefsToDelete: ContentHref[],
): Array<ReferenceGroupReference> =>
  relationsMap.to[href]?.flatMap((childRel) => {
    const node = getReferenceNode(
      relationsMap,
      childRel.from.href,
      externalData,
      externalRelationsMap,
      externalContent,
    );
    if (!node) {
      return [];
    }
    return { ...node, isDeletedByProposal: proposedHrefsToDelete.includes(node.referenceNodeHref) };
  }) || [];

export const selectPersonReferenceNode = createTypedSelector(
  [
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state, href: ContentHref) => selectContentItem(state, href),
    (state) => selectPersons(state),
    (state) => selectFailedOrMissingResources(state),
  ],
  (relationsMap, content, personsMap, failedOrmissingHrefs): PersonReferenceNode => {
    const referenceRelation =
      relationsMap.from[content.$$meta.permalink].find(isReferencesRelation);
    const referenceHref = referenceRelation?.to.href;
    const reference = referenceHref && (personsMap[referenceHref] as Person | undefined);

    let person: PersonReferenceNode['person'] = null;

    if (reference) {
      person = {
        href: reference.$$meta.permalink,
        fullName: `${reference.firstName} ${reference.lastName}`,
        email: reference.$$email,
        profilePicture: reference.attachments?.find(
          (a) => a.$$expanded.name === 'profile-small.jpg',
        )?.$$expanded.href,
      };
    }
    const loading = !person && !failedOrmissingHrefs.includes(referenceHref as PersonHref);

    return {
      href: content.$$meta.permalink,
      key: content.key,
      type: NodeType.PERSON_REFERENCE,
      customRender: true,
      loading,
      person,
    };
  },
);

export const selectReferenceNode = createTypedSelector(
  [
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state, href: ContentHref) => selectContentItem(state, href),
    (state) => selectAllExternalData(state),
    (state) => selectExternalContentRelationsMap(state),
    (state) => selectExternalContent(state),
    (state) => selectFailedOrMissingResources(state),
  ],
  (
    relationsMap,
    content,
    externalData,
    externalRelationsMap,
    externalContent,
    failedOrmissingHrefs,
  ): ReferenceNode => {
    const referenceRelation =
      relationsMap.from[content.$$meta.permalink].find(isReferencesRelation);
    const referenceHref = referenceRelation?.to.href;

    const referenceNode = getReferenceNode(
      relationsMap,
      content.$$meta.permalink,
      externalData,
      externalRelationsMap,
      externalContent,
    );

    const loading = !referenceNode && !failedOrmissingHrefs.includes(referenceHref as PersonHref);

    return {
      href: content.$$meta.permalink,
      key: content.key,
      type: NodeType.PRO_NEWSLETTER_EVENT,
      customRender: true,
      loading,
      referenceNode,
    };
  },
);

export const selectReferenceNodes = createTypedSelector(
  [
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state, href: ContentHref) => href,
    (state) => selectAllExternalData(state),
    (state) => selectExternalContentRelationsMap(state),
    (state) => selectExternalContent(state),
    (state) => selectProposedContentHrefsToDelete(state),
  ],
  getReferenceNodes,
);

export const selectReferenceGroupNode = createTypedSelector(
  [
    (state, href: ContentHref) => selectContentItem(state, href),
    (state, href: ContentHref) => selectReferenceNodes(state, href),
  ],
  (content: Content, references): ReferenceGroupNode => {
    return {
      href: content.$$meta.permalink,
      key: content.key,
      description: content?.description,
      type: NodeType.REFERENCE_GROUP,
      customRender: true,
      references,
    };
  },
);
